import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatRadioModule } from '@angular/material/radio';
import { FuseCardComponent } from '@fuse/components/card';
import { MatIconModule } from '@angular/material/icon';
import { ILead } from 'app/core/shared-services/shared-interface.types';
import { NgForm, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl  } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { Subject, takeUntil } from 'rxjs';
import { DeliveryDetailsComponent } from './delivery-details/delivery-details.component';
import { SharedMethodsModule } from 'app/core/shared-services/shared-methods.module';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-order-details',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatCheckboxModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, FuseCardComponent, MatRadioModule, DeliveryDetailsComponent],
  templateUrl: './order-details.component.html',
  styleUrls: ['./order-details.component.scss']
})
export class OrderDetailsComponent implements OnInit {

  @ViewChild('orderDetailsNgForm') orderDetailsNgForm: NgForm;
  @ViewChild('addressDetailsNgForm') addressDetailsNgForm: NgForm;

  @Input() lead: ILead;

  public orderDetailsForm: UntypedFormGroup;
  public addressDetailsForm: UntypedFormGroup;

  public isScreenSmall: boolean = false;

  public isSameAsInstallAddress: boolean = true;

  public confirmDeliveryValid: boolean;

  public ResidenceType: string = 'FreeStanding';

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  @Output() confirmOrderEvent = new EventEmitter<any>();

  /**
     * Constructor
     */
  constructor(
    private _formBuilder: UntypedFormBuilder,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
  ) { }


  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  async ngOnInit() {

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
      });

    // Create the form
    this.orderDetailsForm = this._formBuilder.group({
      UnitNumber: [''],
      ComplexName: [''],
      FloorNumber: [''],
      ComplexStreetName: [''],
      SubComplexName: [''],
      ErfNumber: [''],
      BlockName: [''],
      LeadNotes: [''],
      DeliveryAddressSameAsInstallationAddress: [true]

    })

    this.residenceType(null, this.ResidenceType);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  public residenceType(event?: any, value?: string) {

    if(event !== null) {
      this.ResidenceType = event.value;
    } else {
      this.ResidenceType = value;
    }

    if(this.ResidenceType == 'FreeStanding') {
      this.isFormValid(true);
    } else {
      this.isFormValid(false);
    }

    this.orderDetailsForm.reset();

    // this.ResidenceType = event.value;
    
    // Look at a better solution surely ........
    this.orderDetailsForm.get('UnitNumber').clearValidators();
    this.orderDetailsForm.get('ComplexName').clearValidators();
    this.orderDetailsForm.get('ComplexStreetName').clearValidators();
    this.orderDetailsForm.get('SubComplexName').clearValidators();
    this.orderDetailsForm.get('ErfNumber').clearValidators();
    this.orderDetailsForm.get('FloorNumber').clearValidators();
    this.orderDetailsForm.get('BlockName').clearValidators();
    this.orderDetailsForm.get('ComplexStreetName').clearValidators();
    this.orderDetailsForm.get('SubComplexName').clearValidators();

    if (this.ResidenceType == 'Estate') {
    
      this.orderDetailsForm.get('UnitNumber').setValidators([Validators.required]);
      this.orderDetailsForm.get('UnitNumber').updateValueAndValidity();

      this.orderDetailsForm.get('ComplexName').setValidators([Validators.required]);
      this.orderDetailsForm.get('ComplexName').updateValueAndValidity();

      this.orderDetailsForm.get('ComplexStreetName').setValidators([Validators.required]);
      this.orderDetailsForm.get('ComplexStreetName').updateValueAndValidity();

      this.orderDetailsForm.get('SubComplexName').setValidators([Validators.required]);
      this.orderDetailsForm.get('SubComplexName').updateValueAndValidity();
    
    } else if (this.ResidenceType == 'Plot') {
      
      this.orderDetailsForm.reset();
 
      this.orderDetailsForm.get('ErfNumber').setValidators([Validators.required]);
      this.orderDetailsForm.get('ErfNumber').updateValueAndValidity();
      
      this.orderDetailsForm.updateValueAndValidity();

    } else if (this.ResidenceType == 'Apartment') {
    
      this.orderDetailsForm.get('UnitNumber').setValidators([Validators.required]);
      this.orderDetailsForm.get('UnitNumber').updateValueAndValidity();

      this.orderDetailsForm.get('ComplexName').setValidators([Validators.required]);
      this.orderDetailsForm.get('ComplexName').updateValueAndValidity();

      this.orderDetailsForm.get('FloorNumber').setValidators([Validators.required]);
      this.orderDetailsForm.get('FloorNumber').updateValueAndValidity();

      this.orderDetailsForm.get('BlockName').setValidators([Validators.required]);
      this.orderDetailsForm.get('BlockName').updateValueAndValidity();

      this.orderDetailsForm.get('ComplexStreetName').setValidators([Validators.required]);
      this.orderDetailsForm.get('ComplexStreetName').updateValueAndValidity();

      this.orderDetailsForm.get('SubComplexName').setValidators([Validators.required]);
      this.orderDetailsForm.get('SubComplexName').updateValueAndValidity();
    }

  }

  public matCheckBox(event: any) {
    this.isSameAsInstallAddress = event.checked;

    if (!this.isSameAsInstallAddress) {
      this.confirmDeliveryValid = false;
      this.orderDetailsForm.value.DeliveryAddressSameAsInstallationAddress = false;
      this.isFormValid(false);
    } else {
      this.orderDetailsForm.value.DeliveryAddressSameAsInstallationAddress = true;
      this.isFormValid(true);
    }
  }

  public isFormValid(val?: any) {
    if(val == true ) {
      this.confirmOrderEvent.emit(this.orderDetailsForm.value);
    } else if ((val == false || !this.orderDetailsForm.valid) && this.ResidenceType !== 'FreeStanding') {
      this.confirmOrderEvent.emit(null);
    } else {
      this.confirmOrderEvent.emit(this.orderDetailsForm.value);
    }
  }

  public confirmDelivery(val: any) {
    if (val !== null) {
      this.confirmDeliveryValid = true
      if (this.confirmDeliveryValid && this.orderDetailsForm.valid) {
        this.confirmOrderEvent.emit(Object.assign(val, this.orderDetailsForm.value));
      }    
    } else {
      this.confirmOrderEvent.emit(null);
    }
  }

  /**
     * On destroy
     */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

}
